(function () {
  'use strict';

  const colorwayCacheGlobal = new Map();

  function initHeroProductShowcase(section, options) {
    if (!section) return;

    const getCollectionHandle = () => (section.dataset.collectionHandle || '').trim();
    if (!getCollectionHandle()) return;

    const productLimit = parseInt(section.dataset.productLimit, 10) || 4;
    const imageLoading = section.dataset.imageLoading === 'eager' ? 'eager' : 'lazy';
    const reload = options && options.reload;

    const swiperElement = section.querySelector('[data-hero-product-swiper]');
    const productWrapper = section.querySelector('[data-product-wrapper]');
    const progressElement = section.querySelector('[data-hero-product-progress]');
    const progressBarFill = progressElement
      ? progressElement.querySelector('.hero-product-showcase__progress-bar')
      : null;
    const paginationElement = section.querySelector('[data-hero-product-pagination]');

    let swiperInstance = null;
    const colorwayCache = colorwayCacheGlobal;

    const flattenConnections = (object) => {
      if (!(object && typeof object === 'object') || Array.isArray(object)) return object;
      if ('edges' in object && Array.isArray(object.edges)) {
        return object.edges.map((edge) => flattenConnections(edge.node));
      }
      for (const prop in object) object[prop] = flattenConnections(object[prop]);
      return object;
    };

    /**
     * Storefront GraphQL / collections JSON return catalog/retail prices and
     * ignore logged-in wholesale/B2B context. Overlay session-aware prices
     * from the Ajax Product API (same source Liquid uses on PDPs / YMAL).
     */
    const centsToAmountString = (cents) => (Number(cents || 0) / 100).toFixed(2);

    const applyCustomerAwarePricing = (productData, productJson) => {
      if (!productData || !productJson) return productData;

      const currencyCode =
        productData.priceRange?.minVariantPrice?.currencyCode ||
        (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active) ||
        'USD';

      const priceMinCents =
        productJson.price_min != null ? productJson.price_min : productJson.price;
      const priceMinAmount = centsToAmountString(priceMinCents);

      productData.priceRange = {
        minVariantPrice: {
          amount: priceMinAmount,
          currencyCode,
        },
      };

      const compareAtMinCents =
        productJson.compare_at_price_min != null
          ? productJson.compare_at_price_min
          : productJson.compare_at_price;

      if (compareAtMinCents != null && Number(compareAtMinCents) > Number(priceMinCents)) {
        productData.compareAtPriceRange = {
          minVariantPrice: {
            amount: centsToAmountString(compareAtMinCents),
            currencyCode,
          },
        };
      } else {
        productData.compareAtPriceRange = {
          minVariantPrice: {
            amount: '0',
            currencyCode,
          },
        };
      }

      if (Array.isArray(productData.variants) && Array.isArray(productJson.variants)) {
        const priceByVariantId = new Map();
        productJson.variants.forEach((variantJson) => {
          priceByVariantId.set(String(variantJson.id), variantJson.price);
        });

        productData.variants.forEach((variant) => {
          const variantId =
            typeof variant.id === 'string' && variant.id.includes('/')
              ? variant.id.split('/').pop()
              : String(variant.id);
          if (!priceByVariantId.has(variantId)) return;
          variant.price = {
            amount: centsToAmountString(priceByVariantId.get(variantId)),
            currencyCode,
          };
        });
      }

      return productData;
    };

    const hydrateProductsWithCustomerPrices = async (products) => {
      if (!products?.length) return products;

      return Promise.all(
        products.map(async (product) => {
          if (!product?.handle) return product;
          try {
            const response = await fetch(`/products/${product.handle}.js`);
            if (!response.ok) return product;
            const productJson = await response.json();
            return applyCustomerAwarePricing(product, productJson);
          } catch (error) {
            return product;
          }
        }),
      );
    };

    const fetchProducts = async () => {
      const handle = getCollectionHandle();
      if (!handle) return [];
      try {
        const response = await fetch('/api/2024-07/graphql.json', {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            'x-shopify-storefront-access-token': '9f39520505c5a1a7f0bab22fb8a87abf',
          },
          body: JSON.stringify({
            query: `
              query getCollectionProducts($handle: String!, $first: Int!) {
                collection(handle: $handle) {
                  products(first: $first) {
                    edges {
                      node {
                        id
                        handle
                        title
                        tags
                        options {
                          name
                          values
                        }
                        priceRange {
                          minVariantPrice {
                            amount
                            currencyCode
                          }
                        }
                        compareAtPriceRange {
                          minVariantPrice {
                            amount
                            currencyCode
                          }
                        }
                        images(first: 10) {
                          edges {
                            node {
                              url(transform: {maxWidth: 600, maxHeight: 800})
                              width
                              height
                              altText
                            }
                          }
                        }
                        variants(first: 250) {
                          edges {
                            node {
                              id
                              title
                              availableForSale
                              price {
                                amount
                                currencyCode
                              }
                              image {
                                url(transform: {maxWidth: 100, maxHeight: 100})
                                width
                                height
                              }
                              selectedOptions {
                                name
                                value
                              }
                              metafield(namespace: "custom", key: "swatch_image") {
                                value
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            `,
            variables: { 
              handle: handle,
              first: productLimit
            },
          }),
        });

        const json = await response.json();
        const { data, errors } = json;

        if (errors && errors.length > 0) {
          console.warn('Hero Product Showcase GraphQL errors:', errors);
        }

        if (data?.collection?.products?.edges?.length) {
          const productConnection = data.collection.products;
          const products = flattenConnections(productConnection);
          const limit = productLimit;
          return hydrateProductsWithCustomerPrices(products.slice(0, limit));
        }

        if (!data?.collection) {
          console.warn('Hero Product Showcase: Storefront API returned no collection for handle:', handle, '- falling back to storefront JSON.');
        }
      } catch (error) {
        console.warn('Hero Product Showcase: GraphQL fetch failed, falling back to storefront JSON:', error);
      }

      try {
        const handle = getCollectionHandle();
        if (!handle) return [];
        const limit = productLimit;
        const jsonResponse = await fetch('/collections/' + encodeURIComponent(handle) + '/products.json?limit=' + limit);
        if (!jsonResponse.ok) return [];
        const jsonData = await jsonResponse.json();
        const rawProducts = jsonData.products || [];
        if (rawProducts.length === 0) return [];

        const optionName = (opts, i) => {
          if (!opts || !opts[i]) return i === 0 ? 'Option' : 'Option' + (i + 1);
          const o = opts[i];
          return typeof o === 'string' ? o : (o.name || 'Option' + (i + 1));
        };

        const products = rawProducts.map((p) => {
          const opts = p.options || [];
          const variants = (p.variants || []).map((v) => {
            const selectedOptions = [];
            if (v.option1) selectedOptions.push({ name: optionName(opts, 0), value: v.option1 });
            if (v.option2) selectedOptions.push({ name: optionName(opts, 1), value: v.option2 });
            if (v.option3) selectedOptions.push({ name: optionName(opts, 2), value: v.option3 });
            return {
              id: 'gid://shopify/ProductVariant/' + v.id,
              title: v.title,
              availableForSale: v.available !== false,
              price: { amount: String(v.price), currencyCode: 'USD' },
              image: v.image ? { url: v.image.src, width: v.image.width, height: v.image.height } : null,
              selectedOptions: selectedOptions,
            };
          });
          const prices = variants.filter((v) => v.price && v.price.amount).map((v) => parseFloat(v.price.amount));
          const comparePrices = (p.variants || []).map((v) => (v.compare_at_price ? parseFloat(v.compare_at_price) : 0));
          const minPrice = prices.length ? Math.min(...prices) : 0;
          const maxCompare = comparePrices.length ? Math.max(...comparePrices) : 0;
          const hasCompare = maxCompare > minPrice && maxCompare > 0;
          return {
            id: typeof p.id === 'string' && p.id.includes('/') ? p.id : 'gid://shopify/Product/' + p.id,
            handle: p.handle,
            title: p.title,
            tags: p.tags || [],
            priceRange: { minVariantPrice: { amount: String(minPrice), currencyCode: 'USD' } },
            compareAtPriceRange: hasCompare ? { minVariantPrice: { amount: String(maxCompare), currencyCode: 'USD' } } : null,
            images: (p.images || []).map((img) => ({
              url: img.src,
              altText: img.alt || p.title,
              width: img.width,
              height: img.height,
            })),
            variants: variants,
          };
        });

        return hydrateProductsWithCustomerPrices(products.slice(0, limit));
      } catch (fallbackError) {
        console.error('Hero Product Showcase: Fallback fetch failed:', fallbackError);
        return [];
      }
    };

    const validateImageUrl = (url) => {
      if (!url) return Promise.resolve(false);
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = url;
        setTimeout(() => resolve(false), 2000);
      });
    };

    const getSwatchFromVariant = (variant) => {
      if (!variant) return null;

      let metafieldValue = null;

      if (variant.metafield) {
        if (variant.metafield.value !== null && variant.metafield.value !== undefined) {
          metafieldValue = variant.metafield.value;
        } else if (typeof variant.metafield === 'string') {
          metafieldValue = variant.metafield;
        }
      } else if (variant.metafields && variant.metafields.swatch_image) {
        metafieldValue = variant.metafields.swatch_image;
      }

      if (metafieldValue !== null && metafieldValue !== undefined) {
        const trimmedValue = String(metafieldValue).trim();
        if (
          trimmedValue &&
          trimmedValue.length > 0 &&
          trimmedValue !== 'null' &&
          trimmedValue !== 'undefined' &&
          trimmedValue !== '' &&
          (trimmedValue.startsWith('http://') || trimmedValue.startsWith('https://'))
        ) {
          return trimmedValue;
        }
      }
      return null;
    };

    const getColorTag = (product) => {
      if (!product.tags || !Array.isArray(product.tags)) return null;

      for (const tag of product.tags) {
        if (tag.includes('_') && tag !== 'new_arrival' && tag !== 'Endless_Summer') {
          return tag;
        }
      }
      return null;
    };

    const fetchColorwaysByTag = async (tag) => {
      if (colorwayCache.has(tag)) {
        return colorwayCache.get(tag);
      }

      try {
        const response = await fetch('/api/2024-07/graphql.json', {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            'x-shopify-storefront-access-token': '9f39520505c5a1a7f0bab22fb8a87abf',
          },
          body: JSON.stringify({
            query: `
              query getProductsByTag($query: String!) {
                colorways: products(query: $query, first: 50) {
                  edges {
                    node {
                      handle
                      title
                      images(first: 2) {
                        edges {
                          node {
                            width
                            height
                            url(transform: {maxHeight: 100, maxWidth: 100})
                          }
                        }
                      }
                      variants(first: 250) {
                        edges {
                          node {
                            title
                            availableForSale
                            image {
                              url(transform: {maxHeight: 100, maxWidth: 100})
                              width
                              height
                            }
                            selectedOptions {
                              name
                              value
                            }
                            metafield(namespace: "custom", key: "swatch_image") {
                              value
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            `,
            variables: { query: `tag:${tag}` },
          }),
        });

        const { data } = await response.json();
        const colorwayProducts = flattenConnections(data?.colorways || []);

        colorwayCache.set(tag, colorwayProducts);
        return colorwayProducts;
      } catch (error) {
        console.error('Error fetching colorways:', error);
        return [];
      }
    };

    const getColorOptions = async (product) => {
      try {
        const colorTag = getColorTag(product);
        if (!colorTag) return [];

        const colorwayProducts = await fetchColorwaysByTag(colorTag);
        if (colorwayProducts.length === 0) return [];

        const colorMap = new Map();

        const colorwayPromises = colorwayProducts.map(async (colorwayProduct) => {
          let primaryImageUrl = null;
          let fallbackImageUrl = null;

          if (colorwayProduct.variants && colorwayProduct.variants.length > 0) {
            const variantWithImage = colorwayProduct.variants.find((v) => v.image?.url);
            if (variantWithImage?.image?.url) {
              fallbackImageUrl = variantWithImage.image.url;
            } else if (colorwayProduct.variants[0]?.image?.url) {
              fallbackImageUrl = colorwayProduct.variants[0].image.url;
            }
          }

          if (!fallbackImageUrl && colorwayProduct.images?.[0]?.url) {
            fallbackImageUrl = colorwayProduct.images[0].url;
          }

          if (colorwayProduct.variants && colorwayProduct.variants.length > 0) {
            for (const variant of colorwayProduct.variants) {
              const swatch = getSwatchFromVariant(variant);
              if (swatch) {
                try {
                  const isValid = await Promise.race([
                    validateImageUrl(swatch),
                    new Promise((resolve) => setTimeout(() => resolve(false), 1500)),
                  ]);

                  if (isValid) {
                    primaryImageUrl = swatch;
                    break;
                  }
                } catch (e) {
                  continue;
                }
              }
            }
          }

          if (!primaryImageUrl && fallbackImageUrl) {
            primaryImageUrl = fallbackImageUrl;
          }

          if (!primaryImageUrl && colorwayProduct.images?.[0]?.url) {
            primaryImageUrl = colorwayProduct.images[0].url;
            if (!fallbackImageUrl) {
              fallbackImageUrl = primaryImageUrl;
            }
          }

          return {
            name: colorwayProduct.title,
            handle: colorwayProduct.handle,
            image: primaryImageUrl || fallbackImageUrl,
            fallback: fallbackImageUrl || primaryImageUrl,
            available: colorwayProduct.variants?.some((v) => v.availableForSale) || false,
          };
        });

        const colorOptions = await Promise.all(colorwayPromises);

        colorOptions.forEach((color) => {
          if (!colorMap.has(color.handle)) {
            colorMap.set(color.handle, color);
          }
        });

        return Array.from(colorMap.values());
      } catch (error) {
        console.warn('Error getting color options:', error);
        return [];
      }
    };

    const formatPrice = (amount, currencyCode) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode || 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(parseFloat(amount));
    };

    const renderProductCard = async (product, colorOptions = []) => {
      if (typeof window.renderProductShowcaseCard === 'function') {
        const showBadge = section.dataset.showBadge === 'true';
        const cardHTML = window.renderProductShowcaseCard(
          product,
          colorOptions,
          'hero-product-showcase',
          showBadge,
          false,
          true,
          {
            mobileWidths: [280, 400, 600],
            desktopWidths: [280, 320, 350],
            mobileSizes: '50vw',
            desktopSizes: '280px',
          },
          imageLoading,
        );
        return `<div class="swiper-slide">${cardHTML}</div>`;
      }
      
      console.warn('renderProductShowcaseCard not available, retrying...');
      return '';
    };

    const hydrateDeferredCardImages = () => {
      const deferredImages = section.querySelectorAll('.hero-product-showcase__card-image--defer');
      deferredImages.forEach((img) => {
        if (!img || !img.dataset) return;
        const picture = img.closest('picture');
        if (picture) {
          picture.querySelectorAll('source[data-srcset]').forEach((source) => {
            if (source?.dataset.srcset) {
              source.srcset = source.dataset.srcset;
              source.removeAttribute('data-srcset');
            }
          });
        }
        if (img.dataset.src) {
          img.src = img.dataset.src;
          img.removeAttribute('data-src');
        }
        if (img.dataset.srcset) {
          img.srcset = img.dataset.srcset;
          img.removeAttribute('data-srcset');
        }
        img.classList.remove('hero-product-showcase__card-image--defer');
      });
    };

    const initSwymWishlistButtons = (products) => {
      document.dispatchEvent(new CustomEvent('swym:collections-loaded'));

      const validProducts = Array.isArray(products) ? products : [];
      if (!validProducts.length) return;

      const maxWaitMs = 4000;
      const pendingHandles = new Set(
        validProducts
          .map((product) => product?.handle)
          .filter((handle) => typeof handle === 'string' && handle.length > 0),
      );
      if (!pendingHandles.size) return;

      let intervalId = null;
      let timeoutId = null;
      let observer = null;

      const resolveHandle = (handle) => {
        const card = section.querySelector(`.hero-product-showcase__card[data-product-handle="${handle}"]`);
        if (!card) return false;

        const wishlistButton =
          card.querySelector('.swym-wishlist-collections-v2-container')
          || card.nextElementSibling?.querySelector('.swym-wishlist-collections-v2-container')
          || card.parentElement?.querySelector('.swym-wishlist-collections-v2-container');

        if (!wishlistButton) return false;

        card.appendChild(wishlistButton);
        return true;
      };

      const flushPending = () => {
        pendingHandles.forEach((handle) => {
          if (resolveHandle(handle)) {
            pendingHandles.delete(handle);
          }
        });
      };

      const cleanup = () => {
        if (intervalId) clearInterval(intervalId);
        if (timeoutId) clearTimeout(timeoutId);
        if (observer) observer.disconnect();
      };

      flushPending();
      if (!pendingHandles.size) return;

      observer = new MutationObserver(() => {
        flushPending();
        if (!pendingHandles.size) cleanup();
      });
      observer.observe(section, { childList: true, subtree: true });

      intervalId = setInterval(() => {
        flushPending();
        if (!pendingHandles.size) cleanup();
      }, 300);

      timeoutId = setTimeout(() => {
        cleanup();
      }, maxWaitMs);
    };

    const initProductImageSwipers = () => {
      if (typeof window.initProductShowcaseCardImageSwipers === 'function') {
        window.initProductShowcaseCardImageSwipers(section);
      }
    };

    const initMainSwiper = () => {
      if (!swiperElement || typeof Swiper === 'undefined') {
        if (typeof Swiper === 'undefined') {
          setTimeout(() => initMainSwiper(), 100);
        }
        return;
      }

      if (typeof window.initHeroProductShowcaseMainSwiper !== 'function') {
        setTimeout(() => initMainSwiper(), 100);
        return;
      }

      swiperInstance = window.initHeroProductShowcaseMainSwiper({
        section,
        swiperElement,
        progressElement,
        progressBarFill,
        paginationElement,
        existingInstance: swiperInstance,
        onProductImagesInit: initProductImageSwipers,
      });
    };

    const loadProducts = async () => {
      try {
        const products = await fetchProducts();
        if (products.length === 0) {
          const skeletons = productWrapper.querySelectorAll('[data-skeleton]');
          skeletons.forEach((skeleton) => skeleton.remove());
          return;
        }

        const colorTagMap = new Map();
        products.forEach((product) => {
          const colorTag = getColorTag(product);
          if (colorTag && !colorTagMap.has(colorTag)) {
            colorTagMap.set(colorTag, []);
          }
          if (colorTag) {
            colorTagMap.get(colorTag).push(product);
          }
        });

        const colorwayPromises = Array.from(colorTagMap.keys()).map((tag) => fetchColorwaysByTag(tag));
        await Promise.all(colorwayPromises);

        const colorOptionsPromises = products.map((product) => getColorOptions(product));
        const allColorOptions = await Promise.all(colorOptionsPromises);

        let retries = 0;
        while (typeof window.renderProductShowcaseCard !== 'function' && retries < 10) {
          await new Promise(resolve => setTimeout(resolve, 100));
          retries++;
        }
        
        if (typeof window.renderProductShowcaseCard !== 'function') {
          console.error('renderProductShowcaseCard function not available');
          const skeletons = productWrapper.querySelectorAll('[data-skeleton]');
          skeletons.forEach((skeleton) => skeleton.remove());
          return;
        }
        
        const cardPromises = products.map((product, index) => renderProductCard(product, allColorOptions[index]));
        const cardHTMLs = await Promise.all(cardPromises);

        const skeletons = productWrapper.querySelectorAll('[data-skeleton]');
        skeletons.forEach((skeleton) => skeleton.remove());

        productWrapper.innerHTML = cardHTMLs.join('');

        setTimeout(() => {
          if (typeof window.initCardSkeletons === 'function') {
            window.initCardSkeletons();
          }
        }, 50);

        hydrateDeferredCardImages();

        initMainSwiper();

        setTimeout(() => {
          if (swiperInstance) {
            swiperInstance.update();
          }
          initProductImageSwipers();
        }, 100);

        const productDataPromises = products.map((product) => loadProductData(product.handle));
        await Promise.all(productDataPromises);

        setTimeout(() => {
          products.forEach((product) => {
            const card = section.querySelector(`.hero-product-showcase__card[data-product-handle="${product.handle}"]`);
            if (card && card.getAttribute('data-loading') === 'true') {
              const imageWrapper = card.querySelector('.hero-product-showcase__card-image-wrapper');
              const firstImage = imageWrapper?.querySelector('img');
              if (firstImage && firstImage.complete && firstImage.naturalHeight > 0) {
                card.setAttribute('data-loading', 'false');
              }
            }
          });
        }, 500);

        setTimeout(initSwymWishlistButtons(products), 500);
      } catch (error) {
        console.error('Error loading products:', error);
        const skeletons = productWrapper.querySelectorAll('[data-skeleton]');
        skeletons.forEach((skeleton) => skeleton.remove());
      }
    };

    const loadProductData = async (productHandle) => {
      try {
        const url = `/products/${productHandle}?view=review`;
        const response = await fetch(url);
        const html = await response.text();
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        const customText = tempDiv.querySelector('.custom_display_text');
        const customContainer = section.querySelector(
          `.hero-product-showcase__card[data-product-handle="${productHandle}"] .hero-product-showcase__card-custom-title`,
        );

        if (customText && customContainer) {
          const textElement = customText.cloneNode(true);
          if (textElement.textContent) {
            if (typeof theme !== 'undefined' && theme.utils && theme.utils.toProperCase) {
              textElement.textContent = theme.utils.toProperCase(textElement.textContent);
            }
          }
          customContainer.innerHTML = textElement.outerHTML;
        }

        const reviewBadge = tempDiv.querySelector('.jdgm-preview-badge');
        const reviewContainer = section.querySelector(
          `.hero-product-showcase__card[data-product-handle="${productHandle}"] .hero-product-showcase__card-review`,
        );

        if (reviewBadge && reviewContainer) {
          const prevBadge = reviewBadge.querySelector('.jdgm-prev-badge');
          const numberOfReviews = prevBadge ? parseInt(prevBadge.getAttribute('data-number-of-reviews') || '0', 10) : 0;

          if (numberOfReviews > 0) {
            reviewContainer.innerHTML = reviewBadge.outerHTML;

            const badgeText = reviewContainer.querySelector('.jdgm-prev-badge__text');
            if (badgeText) {
              let text = badgeText.textContent.trim();
              let match = text.match(/(\d+)\s+review(s)?/i);
              if (match) {
                let number = match[1];
                badgeText.textContent = `(${number})`;
              }
            }

            if (typeof window.jdgm !== 'undefined' && window.jdgm.loadWidgets) {
              window.jdgm.loadWidgets();
            }
          } else {
            if (reviewContainer) {
              reviewContainer.style.display = 'none';
            }
          }
        }
      } catch (error) {
        console.warn(`Failed to load data for ${productHandle}:`, error);
      }
    };

    const handleHeroProductCardClick = (clickEvent) => {
      const addToCartButton = clickEvent.target.closest('.hero-product-showcase__card-add-to-cart');
      const wishlistButton = clickEvent.target.closest('.hero-product-showcase__card-wishlist, .swym-wishlist-collections-v2, .swym-wishlist-collections-v2-container');
      const imageArrow = clickEvent.target.closest('.hero-product-showcase__card-image-arrow');
      const colorSwatch = clickEvent.target.closest('.hero-product-showcase__card-color-swatch');
      const productCard = clickEvent.target.closest('.hero-product-showcase__card');

      if (addToCartButton || wishlistButton || imageArrow || colorSwatch) {
        return;
      }

      if (productCard) {
        const productHandle = productCard.getAttribute('data-product-handle');
        if (productHandle) {
          window.location.href = `/products/${productHandle}`;
        }
      }
    };

    section.addEventListener('click', (e) => {
      const addToCartBtn = e.target.closest('.hero-product-showcase__card-add-to-cart');
      if (!addToCartBtn) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const productHandle = addToCartBtn.dataset.productHandle;
      const productId = addToCartBtn.getAttribute('data-product-id');

      if (!productHandle) return;

      const prefetched =
        typeof window.parseQuickAddProductFromButton === 'function'
          ? window.parseQuickAddProductFromButton(addToCartBtn)
          : null;

      if (typeof window.openQuickAddModalProductCards === 'function') {
        window.openQuickAddModalProductCards(productHandle, productId, prefetched, addToCartBtn);
      } else {
        window.location.href = '/products/' + productHandle;
      }
    });

    section.addEventListener('click', handleHeroProductCardClick);

    const initSwiperWithSkeleton = () => {
      if (!swiperElement) {
        setTimeout(() => initSwiperWithSkeleton(), 50);
        return;
      }

      if (typeof Swiper === 'undefined' || typeof window.initHeroProductShowcaseMainSwiper !== 'function') {
        setTimeout(() => initSwiperWithSkeleton(), 100);
        return;
      }

      if (swiperInstance && productWrapper.children.length > 0) {
        return;
      }

      try {
        swiperInstance = window.initHeroProductShowcaseMainSwiper({
          section,
          swiperElement,
          progressElement,
          progressBarFill,
          paginationElement,
          existingInstance: swiperInstance,
          skeletonMode: true,
        });

        setTimeout(() => {
          if (swiperInstance) {
            swiperInstance.update();
          }
        }, 50);
      } catch (error) {
        console.error('Error initializing skeleton swiper:', error);
        setTimeout(() => initSwiperWithSkeleton(), 200);
      }
    };

    const initializeSkeleton = () => {
      if (!swiperElement || !productWrapper) {
        setTimeout(() => initializeSkeleton(), 50);
        return;
      }

      const skeletons = productWrapper.querySelectorAll('[data-skeleton]');
      if (skeletons.length === 0) {
        return;
      }

      initSwiperWithSkeleton();
    };

    initializeSkeleton();
    setTimeout(() => loadProducts(), reload ? 100 : 300);
  }

  function initAll() {
    document.querySelectorAll('[data-hero-product-showcase]').forEach(function (section) {
      initHeroProductShowcase(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    const sectionId = e.detail && e.detail.sectionId;
    if (!sectionId) return;
    const section = document.querySelector('[data-hero-product-showcase][data-section-id="' + sectionId + '"]');
    if (!section) return;
    setTimeout(function () {
      initHeroProductShowcase(section, { reload: true });
    }, 100);
  });
})();
